import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import { getOnboardingStepData } from '../../utils/onboardingUtils';
import { hasCompletedOnboarding, getOnboardingRoute } from '../../utils/AuthorizationCheck';

const RoastLevelSelection = ({auth}) => {        
    const history = useHistory();
    const [selectedRoastLevel, setSelectedRoastLevel] = useState(''); 
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Roast level options
    const roastLevels = [
        {
            id: 'participation_trophy',
            emoji: '🏆',
            title: 'Participation Trophy',
            description: 'A lighthearted roast where even the losers get participation trophies',
            selectable: true
        },
        {
            id: 'sunburn',
            emoji: '🔥',
            title: 'Sunburn',
            description: 'Roasting with a sting that lasts',
            selectable: true
        },
        {
            id: 'red_band',
            emoji: '🤬',
            title: 'Red Band (coming soon)',
            description: 'Profanity-laced roasts that would make your group chat blush',
            selectable: false
        }
    ];

    useEffect(() => {                
        // Check if user is authenticated and has completed onboarding
        if (auth?.user?.id && hasCompletedOnboarding(auth)) {
            history.push('/dashboard');
            return;
        }
        
        // Check if user should be on a different onboarding step
        if (auth?.user?.id && !hasCompletedOnboarding(auth)) {
            const correctRoute = getOnboardingRoute(auth);
            if (correctRoute !== '/onboarding/roast-level') {
                history.push(correctRoute);
                return;
            }
        }
        
        // No need to check previous step - database step progression is handled by backend APIs
        
        // Load existing data from session storage if available
        const existingData = getOnboardingStepData('roast-level');
        if (existingData?.roast_level) {
            setSelectedRoastLevel(existingData.roast_level);
        }
    }, [auth, history]);

    const handleRoastLevelSelect = (roastLevelId) => {
        const roastLevel = roastLevels.find(level => level.id === roastLevelId);
        if (!roastLevel.selectable) return;
        
        // If clicking the already selected roast level, deselect it
        if (selectedRoastLevel === roastLevelId) {
            setSelectedRoastLevel('');
        } else {
            setSelectedRoastLevel(roastLevelId);
        }
    };

    const handleContinue = async () => {
        if (!selectedRoastLevel) return;
        
        setIsSubmitting(true);
        
        try {
            // Save roast level selection to backend if authenticated
            if (auth?.user?.id) {
                // Get league data from database
                const response = await axios.get('/api/get/user-league-data');
                if (!response.data.success) {
                    throw new Error('League information not found. Please restart onboarding from league selection.');
                }
                
                const platform_league_id = response.data.platform_league_id;
                
                await axios.post('/api/post/roast-level', {
                    roast_level: selectedRoastLevel,
                    platform_league_id: platform_league_id
                });
                
                // Update onboarding step to 6 after successful roast level save
                await axios.post('/api/post/onboarding-step/6');
                
                // Small delay to allow database update to propagate and prevent flash
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            // Move to next step
            history.push('/onboarding/league-context');
        } catch (error) {
            console.error('Error saving roast level:', error);
            // Show user-friendly error message
            alert('Failed to save roast level selection. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };


    const getProgressPercentage = () => {
        // Roast level is step 5 of 8, so (5-1)/8 = 50%
        return 50;
    };

    return (
        <>
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Choose Your Roast Level</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col>
                                <div className="roast-level-options">
                                    {roastLevels.map(level => (
                                        <div
                                            key={level.id}
                                            className={`roast-level-card ${selectedRoastLevel === level.id ? 'selected' : ''} ${!level.selectable ? 'coming-soon' : ''}`}
                                            onClick={() => handleRoastLevelSelect(level.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleRoastLevelSelect(level.id);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={level.selectable ? 0 : -1}
                                            aria-label={`Select ${level.title} roast level`}
                                            aria-pressed={selectedRoastLevel === level.id}
                                        >
                                            <div className="roast-level-emoji">{level.emoji}</div>
                                            <h4 className="roast-level-title">{level.title}</h4>
                                            <p className="roast-level-description">{level.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        <Row className='align-items-center mt-4 justify-content-center'>
                            <Col xs="auto">
                                <Button 
                                    onClick={handleContinue}
                                    disabled={!selectedRoastLevel || isSubmitting}
                                    className={`btn-lg mx-2 onboarding-nav-btn ${!selectedRoastLevel ? 'btn-secondary' : 'btn-primary-gradient'}`}
                                >
                                    {isSubmitting ? 'Saving...' : 'Next →'}
                                </Button>
                            </Col>
                        </Row>                                                 
                    </Col>                            
                </Row>
            </div>
        </>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(RoastLevelSelection);
